import { Component, ElementRef, inject, linkedSignal, output, input, viewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonV3Component, ComplexDialogV3Service, IconV3Component } from '@gea/digital-ui-lib';
import { BRCBaseComponent } from '@brc/hrt-base-component';
import { ThumbnailDialogComponent } from './thumbnail-dialog';

import { ChangeDetectionStrategy } from '@angular/core';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gea-hrt-images-preview',
  imports: [TranslatePipe, ButtonV3Component, IconV3Component],
  styleUrl: './images-preview.component.scss',
  template: `
    <div class="image-preview-container">
      <geaui-button-v3 type="secondary" preIcon="img" (click)="triggerFilePicker()" [sending]="sending" [disabled]="disabled()">
        {{ 'SHARED.IMAGES_PREVIEW.ADD_IMAGES' | translate }}
      </geaui-button-v3>

      <input #fileInput type="file" hidden multiple accept="image/*" (change)="onFileSelected($event)" [disabled]="disabled()" />

      <div class="flex">
        @for (image of displayedImages(); track $index; let i = $index) {
          <div class="image-preview">
            <geaui-icon-v3
              file="24px_file-image"
              color="black"
              (click)="openImagePreview(image)"
              [title]="'SHARED.IMAGES_PREVIEW.PREVIEW' | translate" />
            <div class="name">{{ 'UI-LIB.ATTACHMENTS.ATTACHMENT' | translate: { index: i } }}</div>
            <geaui-button-v3
              type="secondary"
              information="error"
              data-testid="btn-delete"
              class="delete-button"
              preIcon="trash-can"
              [disabled]="disabled()"
              (click)="removeImage(i)"
              [title]="'SHARED.IMAGES_PREVIEW.DELETE_IMAGE' | translate"></geaui-button-v3>
          </div>
        }
      </div>

      @if (show5mbWarning) {
        <div class="warning">
          {{ 'SHARED.IMAGES_PREVIEW.FILE_TOO_LARGE' | translate }}
        </div>
      }
    </div>
  `,
})
export class ImagesPreviewComponent extends BRCBaseComponent {
  readonly images = input<string[]>([]);
  readonly displayedImages = linkedSignal(() => this.images());
  readonly imagesChange = output<string[]>();
  readonly disabled = input(false);

  readonly fileInputRef = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  show5mbWarning = false;
  sending = false;

  protected readonly dialogService = inject(ComplexDialogV3Service);

  triggerFilePicker(): void {
    if (this.disabled()) return;

    this.fileInputRef().nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.sending = true;

    const selectedFiles = Array.from(input.files);
    const newImages: string[] = [];
    let hasLargeFile = false;
    const validFiles = selectedFiles.filter((file) => {
      const sizeInMB = file.size / 1024 / 1024;
      if (sizeInMB > 5) {
        hasLargeFile = true;
        return false;
      }

      return true;
    });

    this.show5mbWarning = hasLargeFile;

    if (!validFiles.length) {
      this.sending = false;
      input.value = '';
      return;
    }

    let processedFiles = 0;
    const finalizeBatch = () => {
      processedFiles += 1;

      if (processedFiles === validFiles.length) {
        this.updateFormControl([...new Set([...this.displayedImages(), ...newImages])]);
      }
    };

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          newImages.push(reader.result);
        }

        finalizeBatch();
      };
      reader.onerror = finalizeBatch;

      reader.readAsDataURL(file);
    });

    input.value = ''; // Reset input so same file can be reselected if needed
  }

  removeImage(index: number): void {
    if (this.disabled()) return;

    const updatedImages = [...this.displayedImages()];
    updatedImages.splice(index, 1);
    this.updateFormControl(updatedImages);
  }

  async openImagePreview(imageData: string): Promise<void> {
    this.dialogService.open(
      {
        header: 'SHARED.IMAGES_PREVIEW.PREVIEW',
        closable: true,
        hideButtons: true,
        data: { imageData },
        variant: 'custom',
        width: '80vw',
        height: '80vh',
      },
      ThumbnailDialogComponent
    );
  }

  private updateFormControl(updatedImages: string[]): void {
    this.displayedImages.set(updatedImages);
    this.imagesChange.emit(updatedImages);
    this.sending = false;
  }
}
